import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  fetchLabPreferenceScreenshots,
  labPreferenceScreenshotUrl,
  uploadLabPreferenceScreenshots,
  type LabPreferenceScreenshot,
} from "../../store/admin/labPreferenceSlice";
import { useToast } from "../utilities/Toast";
import api from "../../utils/api";

type Props = {
  token: string;
  canUpload: boolean;
  dark?: boolean;
};

const AuthenticatedImage = ({
  token,
  fileId,
  alt,
  dark,
}: {
  token: string;
  fileId: string;
  alt: string;
  dark?: boolean;
}) => {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    const load = async () => {
      try {
        const res = await api.get(labPreferenceScreenshotUrl(token, fileId), {
          responseType: "blob",
        });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setSrc(objectUrl);
      } catch {
        if (!cancelled) setSrc(null);
      }
    };

    void load();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [token, fileId]);

  if (!src) {
    return (
      <div
        className={`flex h-36 items-center justify-center rounded-xl text-xs ${
          dark ? "bg-[#1a1b1f] text-[#8b8d93]" : "bg-[#F8F8F6] text-[#8A8A85]"
        }`}
      >
        Loading…
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={`h-36 w-full rounded-xl object-cover border ${
        dark ? "border-[#2e3036]" : "border-[#E8E8E4]"
      }`}
    />
  );
};

const LabPreferenceScreenshots = ({ token, canUpload, dark }: Props) => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { screenshots, screenshotsLoading, uploadingScreenshots } =
    useAppSelector((state) => state.labPreference);

  useEffect(() => {
    if (!token) return;
    void dispatch(fetchLabPreferenceScreenshots(token));
  }, [dispatch, token]);

  const onPickFiles = async (files: FileList | null) => {
    if (!files?.length || !canUpload) return;
    const list = Array.from(files).filter((f) =>
      f.type.startsWith("image/")
    );
    if (!list.length) {
      showToast("Please select image files only", "error");
      return;
    }
    try {
      await dispatch(
        uploadLabPreferenceScreenshots({ token, files: list })
      ).unwrap();
      showToast("Screenshots uploaded", "success");
      void dispatch(fetchLabPreferenceScreenshots(token));
    } catch (err: unknown) {
      showToast(
        typeof err === "string" ? err : "Failed to upload screenshots",
        "error"
      );
    }
  };

  return (
    <div
      className={`mt-6 rounded-2xl border p-4 ${
        dark
          ? "border-[#2e3036] bg-[#1a1b1f]"
          : "border-[#E8E8E4] bg-[#F8F8F6]"
      }`}
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3
            className={`text-sm font-semibold ${
              dark ? "text-white" : "text-[#4A4A48]"
            }`}
          >
            Preference Screenshots
          </h3>
          <p className={`text-xs ${dark ? "text-[#8b8d93]" : "text-[#8A8A85]"}`}>
            Stored in this lab&apos;s Box &quot;lab preferences&quot; folder
          </p>
        </div>
        {canUpload ? (
          <>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                void onPickFiles(e.target.files);
                e.target.value = "";
              }}
            />
            <button
              type="button"
              disabled={uploadingScreenshots}
              onClick={() => inputRef.current?.click()}
              className={`rounded-xl px-3 py-2 text-xs font-medium text-white disabled:opacity-60 ${
                dark
                  ? "bg-[#e11d2e] hover:bg-[#c31c30]"
                  : "bg-[#2fa9bf] hover:bg-[#2296aa]"
              }`}
            >
              {uploadingScreenshots ? "Uploading…" : "Upload screenshots"}
            </button>
          </>
        ) : null}
      </div>

      {screenshotsLoading ? (
        <p className={`text-sm ${dark ? "text-[#8b8d93]" : "text-[#8A8A85]"}`}>
          Loading screenshots…
        </p>
      ) : screenshots.length === 0 ? (
        <p className={`text-sm ${dark ? "text-[#8b8d93]" : "text-[#8A8A85]"}`}>
          No screenshots uploaded yet.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          {screenshots.map((shot: LabPreferenceScreenshot) => (
            <div key={shot.fileId} className="space-y-1">
              <AuthenticatedImage
                token={token}
                fileId={shot.fileId}
                alt={shot.fileName}
                dark={dark}
              />
              <p
                className={`truncate text-[11px] ${
                  dark ? "text-[#8b8d93]" : "text-[#8A8A85]"
                }`}
              >
                {shot.fileName}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LabPreferenceScreenshots;
